// 3D building volumes from Overture Maps — vector tiles over PMTiles range
// requests, rendered with MapLibre's own fill-extrusion. This is the native
// path deliberately chosen over the 3D Tiles route (which needs deck.gl or a
// second engine): one small dependency (pmtiles + fflate) instead of a
// renderer, and the volumes work over EVERY basemap — extruded massing on top
// of Esri imagery is the actual use case (urban context at a strike site).
//
// Heights are honest only in the aggregate: Overture carries a real `height`
// where the source data had one, else we fall back to floors × 3 m, else a
// 5 m hint that says "a building stands here" without claiming how tall. The
// Layers row's subtitle carries that caveat in words — volumes are context,
// never measurements, and nothing here may feed analysis that depends on real
// dimensions.
//
// The release is PINNED: tracking "latest" would let the ground change under
// an open investigation. But old releases do NOT stay up — the bucket keeps
// about two months (2026-09-30: 2026-07-22.0 answered 404 and the layer drew
// nothing), so the pin has to be bumped every month or two. A one-line change.

import * as maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';

const RELEASE = '2026-09-23.1';
const TILESET = 'pmtiles://https://overturemaps-extras-us-west-2.s3.us-west-2'
  + `.amazonaws.com/tiles/${RELEASE}/buildings.pmtiles`;

const SRC = 'overture-buildings';
export const BUILDINGS_LAYER = 'buildings-3d';

// Extrusions start where the tileset's detail does. The archive serves z4–14
// (overzoomed past 14 by MapLibre), but city-scale massing below z13 is noise
// and tile fetches; 13 matches when the basemap's own buildings appear.
const MINZOOM = 13;

let protocolRegistered = false;

export function installBuildings(map: maplibregl.Map): void {
  if (!protocolRegistered) {
    // One protocol registration per page — it is maplibregl-global, and the
    // Studio map would otherwise re-register it.
    maplibregl.addProtocol('pmtiles', new Protocol().tile);
    protocolRegistered = true;
  }
  if (map.getSource(SRC)) return;
  map.addSource(SRC, {
    type: 'vector',
    url: TILESET,
    // ODbL requires this to be displayed; MapLibre's attribution control
    // surfaces source attributions automatically while the source is in use.
    attribution: 'Buildings © Overture Maps Foundation, © OpenStreetMap contributors (ODbL)',
  });
  map.addLayer({
    id: BUILDINGS_LAYER,
    type: 'fill-extrusion',
    source: SRC,
    // The `building` layer only. `building_part` is deliberately skipped: a
    // building with parts ALSO ships its outer shell, so rendering both
    // double-draws every detailed building — and the shell alone is the
    // massing this feature exists to show.
    'source-layer': 'building',
    minzoom: MINZOOM,
    filter: ['!=', ['get', 'is_underground'], true],
    layout: { visibility: 'none' },
    paint: {
      // Neutral, light, translucent: legible over dark imagery without
      // pretending to be photographic. WHO/WHAT stays the entity layers' job.
      'fill-extrusion-color': '#cfd4da',
      'fill-extrusion-opacity': 0.72,
      'fill-extrusion-height': [
        'case',
        ['has', 'height'], ['get', 'height'],
        ['has', 'num_floors'], ['*', ['get', 'num_floors'], 3],
        5,
      ],
      'fill-extrusion-base': ['coalesce', ['get', 'min_height'], 0],
    },
  });
}

// The basemap's own extrusions (Liberty ships building-3d) double-draw under
// ours; hide them while we are on and put back exactly what we found.
let hiddenBase: Map<string, 'visible' | 'none'> = new Map();
let isOn = false;

export function setBuildings(map: maplibregl.Map, on: boolean): void {
  if (!map.getLayer(BUILDINGS_LAYER) || on === isOn) return;
  isOn = on;
  map.setLayoutProperty(BUILDINGS_LAYER, 'visibility', on ? 'visible' : 'none');
  if (on) {
    hiddenBase = new Map();
    for (const layer of map.getStyle().layers ?? []) {
      if (layer.type !== 'fill-extrusion' || layer.id === BUILDINGS_LAYER) continue;
      hiddenBase.set(layer.id,
        (map.getLayoutProperty(layer.id, 'visibility') as 'visible' | 'none' | undefined)
          ?? 'visible');
      map.setLayoutProperty(layer.id, 'visibility', 'none');
    }
  } else {
    for (const [id, vis] of hiddenBase) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', vis);
    }
    hiddenBase = new Map();
  }
}
